/**
 * @fileoverview Widget specification — the shape a dashboard widget saves
 * and re-runs (MOR-906 §7.8).
 *
 * A widget is one metric over one dimension, optionally split by a second:
 * the chart tiles that pin to the Dashboard board. Multi-dimensional tables
 * are pivot reports instead (see pivotSpec.ts).
 *
 * Pure module — the spec type, its default, and the migration that folds the
 * older flat filter fields into the shared boolean filter.
 *
 * Used by:
 * - src/components/timesheets/WidgetBuilder.tsx
 * - src/components/timesheets/FavouriteReportsBoard.tsx
 */

import type { Id } from "@cvx/_generated/dataModel";
import type { QueryToken } from "@cvx/timesheets/query/entryQuery";

export interface WidgetSpec {
  scope: "me" | "org";
  preset: string;
  startDate?: string;
  endDate?: string;
  groupBy: "user" | "job" | "category" | "date" | "week" | "month";
  /** Optional second dimension — stacks the chart ("split by"). */
  seriesBy?: "user" | "job" | "category";
  metric: "hours" | "amount" | "entries" | "writeOff";
  billableOnly: boolean;
  approvedOnly: boolean;
  categoryKeys?: string[];
  jobIds?: Id<"jobs">[];
  userIds?: Id<"users">[];
  /** Bracketed AND/OR expression over entry dimensions. */
  filter?: QueryToken[];
  chart: "bar" | "line" | "table";
}

export const DEFAULT_WIDGET_SPEC: WidgetSpec = {
  scope: "me",
  preset: "this_month",
  groupBy: "category",
  metric: "hours",
  billableOnly: false,
  approvedOnly: false,
  chart: "bar",
};

/**
 * Fold the pre-builder flat filters into boolean filter tokens.
 *
 * The builder offers one filter mechanism, so a widget saved with
 * `billableOnly` or a category list must surface as editable conditions
 * rather than as hidden state the practitioner cannot see. `runReport`
 * still honours the flat fields, so widgets that never pass through the
 * builder (the Dashboard board runs specs directly) keep working untouched.
 */
export function foldLegacyFilters(spec: WidgetSpec): WidgetSpec {
  const added: QueryToken[] = [];
  const push = (token: QueryToken) => {
    if (added.length > 0 || (spec.filter ?? []).length > 0) {
      added.push({ t: "and" });
    }
    added.push(token);
  };

  if (spec.categoryKeys && spec.categoryKeys.length > 0) {
    push({
      t: "cond",
      field: "category",
      negate: false,
      values: spec.categoryKeys,
    });
  }
  if (spec.jobIds && spec.jobIds.length > 0) {
    push({
      t: "cond",
      field: "job",
      negate: false,
      values: spec.jobIds as string[],
    });
  }
  if (spec.userIds && spec.userIds.length > 0) {
    push({
      t: "cond",
      field: "user",
      negate: false,
      values: spec.userIds as string[],
    });
  }
  if (spec.billableOnly) {
    push({ t: "cond", field: "billable", negate: false, values: ["true"] });
  }
  if (spec.approvedOnly) {
    push({ t: "cond", field: "status", negate: false, values: ["approved"] });
  }

  if (added.length === 0) return spec;

  const existing = spec.filter ?? [];
  const openToken: QueryToken = { t: "open" };
  const closeToken: QueryToken = { t: "close" };
  // Bracket a multi-part existing expression so appending AND cannot
  // silently rebind an OR at its top level.
  const wrapped =
    existing.length > 1 ? [openToken, ...existing, closeToken] : existing;

  return {
    ...spec,
    billableOnly: false,
    approvedOnly: false,
    categoryKeys: undefined,
    jobIds: undefined,
    userIds: undefined,
    filter: [...wrapped, ...added],
  };
}
